import { OverlayContainer } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { enumGlobalAlertType } from '@app/data-layer/enums/common';
import { GlobalAlertModel } from '@app/data-layer/models/common';
import { NzNotificationRef, NzNotificationService } from 'ng-zorro-antd/notification';
import { ReplaySubject } from 'rxjs';

@Component({
  selector: 'app-global-alert-message',
  templateUrl: './global-alert-message.component.html',
  styleUrls: ['./global-alert-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalAlertMessageComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public open: boolean = false;
  @Input() public placement: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'bottom' = 'bottom';
  @Input() public type: 'error' | 'warning' | 'info' = 'error';
  @Input() public globalAlerts: GlobalAlertModel[] = [];
  @Input() public isInnerText: boolean = true;
  @Output() public dismiss: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('templateToast', { static: false }) templateToast?: TemplateRef<{}>;

  public currentGlobalAlert: GlobalAlertModel;

  private currentAlertIndex: number = 0;
  private currentToastNotificationRef: NzNotificationRef;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private overlayContainer: OverlayContainer, private notificationService: NzNotificationService) {}

  ngOnInit(): void {
    this.currentGlobalAlert = this.globalAlerts[this.currentAlertIndex];
    this.overlayContainer.getContainerElement().classList.add('over-modal');
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const openPreviousValue = changes.open && changes.open.previousValue;
    const currentValue = changes.open && changes.open.currentValue;

    if (currentValue !== openPreviousValue && !changes.open.firstChange) {
      if (this.open) {
        this.openToast();
      } else {
        this.closeToast();
      }
    }

    if (changes.globalAlerts) {
      this.currentAlertIndex = 0;
      this.currentGlobalAlert = this.globalAlerts[this.currentAlertIndex];
    }
  }

  public ngOnDestroy(): void {
    this.closeToast();
    this.destroy$.next();
  }

  public openToast() {
    this.createToastNotification();
  }

  public closeToast() {
    if (!!this.currentToastNotificationRef) {
      this.notificationService.remove(this.currentToastNotificationRef.messageId);
      this.currentToastNotificationRef = null;
    }
  }

  public getCurrentAlertIndex(id: number): number {
    return this.globalAlerts.findIndex((ga) => ga.id === id);
  }

  public onNavigate(direction: 'left' | 'right') {
    switch (direction) {
      case 'left':
        if (this.currentAlertIndex > 0) {
          this.currentAlertIndex--;
        }
        break;
      case 'right':
        if (this.currentAlertIndex < this.globalAlerts.length) {
          this.currentAlertIndex++;
        }
        break;
      default:
        break;
    }

    this.currentGlobalAlert = this.globalAlerts[this.currentAlertIndex];
  }

  public onDismiss(id: number) {
    this.dismiss.emit(id);
  }

  public get stepsDisplayCounter(): string {
    return `${this.getCurrentAlertIndex(this.currentGlobalAlert.id) + 1}/${this.globalAlerts.length}`;
  }

  public get isMultipleAlerts(): boolean {
    return this.globalAlerts.length > 1;
  }

  public get isFirstAlert(): boolean {
    return this.currentAlertIndex === 0;
  }

  public get isLastAlert(): boolean {
    return this.currentAlertIndex === this.globalAlerts.length - 1;
  }

  public get currentAlertCssClass(): string {
    switch (this.currentGlobalAlert?.type) {
      case enumGlobalAlertType.Error:
        return 'error';
      case enumGlobalAlertType.Warning:
        return 'warning';
      case enumGlobalAlertType.Information:
        return 'info';
      default:
        return this.type;
    }
  }

  public get currentAlertIconSrc(): string {
    switch (this.currentGlobalAlert?.type) {
      case enumGlobalAlertType.Warning:
        return 'assets/icons/alert_warning.svg';
      case enumGlobalAlertType.Information:
        return 'assets/icons/alert_info.svg';
      case enumGlobalAlertType.Error:
      default:
        return 'assets/icons/alert_error.svg';
    }
  }

  private createToastNotification(): void {
    const notification = this.notificationService.template(this.templateToast!, {
      nzClass: 'app-global-alert',
      nzDuration: 0,
      nzPlacement: this.placement,
    });

    this.currentToastNotificationRef = notification;
  }
}
